import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import { getAttendancePerformanceCorrelation } from '../services/insightsService';
import './CorrelationInsights.css';

const CorrelationInsights = ({ halaqaId, period }) => {
  const [correlationData, setCorrelationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (halaqaId && user) {
      fetchCorrelationData();
    }
  }, [halaqaId, selectedPeriod, user]);

  const fetchCorrelationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate required props
      if (!halaqaId) {
        setError('No class selected');
        return;
      }
      
      if (!user) {
        setError('User authentication required');
        return;
      }
      
      // Check if user has appropriate role (admin-only)
      if (!['admin', 'super_admin'].includes(user.role)) {
        setError('You do not have permission to view correlation insights');
        return;
      }

      console.log(`Fetching correlation for halaqa: ${halaqaId}, days: ${selectedPeriod}`);
      
      const response = await getAttendancePerformanceCorrelation(halaqaId, selectedPeriod);
      
      if (response.success) {
        setCorrelationData(response.data);
      } else {
        setError(response.message || 'Failed to fetch correlation data');
      }
      
    } catch (error) {
      console.error('Error fetching correlation data:', error);
      if (error.message === 'Network error') {
        setError('Network connection error. Please try again.');
      } else if (error.message && error.message.includes('403')) {
        setError('You do not have permission to view this data');
      } else {
        setError(error.message || 'Failed to load correlation insights');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(parseInt(event.target.value));
  };

  const formatInsightMessage = (insights) => {
    if (!insights) return 'No correlation analysis available';
    
    if (insights.correlationStrength === 'insufficient') {
      return insights.message;
    }
    
    return insights.message;
  };

  const getCorrelationStrengthColor = (strength) => {
    switch (strength) {
      case 'strong':
        return '#28a745'; // Green
      case 'moderate':
        return '#ffc107'; // Yellow
      case 'weak':
        return '#fd7e14'; // Orange
      case 'insufficient':
        return '#6c757d'; // Gray
      default:
        return '#6c757d';
    }
  };

  const getRatingBarClass = (rating) => {
    if (rating >= 4.0) return 'excellent';
    if (rating >= 2.5) return 'good';
    return 'poor';
  };

  const renderAttendanceBand = (band, title, emoji) => {
    if (!band) return null;
    
    const ratingPercentage = (band.averageRating / 5) * 100;
    const barClass = getRatingBarClass(band.averageRating);
    
    return (
      <div className="correlation-band">
        <div className="band-header">
          <span className="band-emoji">{emoji}</span>
          <span className="band-title">{title}</span>
          <span className="band-range">({band.range})</span>
        </div>
        <div className="band-stats">
          <div className="stat-item">
            <div className="stat-value-container">
              <span className="stat-value">{band.averageRating}</span>
              <span className="stat-unit">/5</span>
            </div>
            <span className="stat-label">avg rating</span>
            <div className="rating-progress-bar">
              <div 
                className={`rating-progress-fill ${barClass}`}
                style={{ width: `${ratingPercentage}%` }}
              ></div>
            </div>
          </div>
          <div className="stat-divider">|</div>
          <div className="stat-item">
            <div className="stat-value-container">
              <span className="stat-value">{band.studentCount}</span>
            </div>
            <span className="stat-label">students</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="correlation-insights">
        <div className="correlation-card">
          <div className="card-header">
            <h3>📊 Attendance Impact on Performance</h3>
          </div>
          <div className="loading-placeholder">
            <div className="loading-shimmer"></div>
            <div className="loading-shimmer"></div>
            <div className="loading-shimmer"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="correlation-insights">
        <div className="correlation-card">
          <div className="card-header">
            <h3>📊 Attendance Impact on Performance</h3>
          </div>
          <div className="error-state">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="correlation-insights">
      <div className="correlation-card">
        <div className="card-header">
          <h3>📊 Attendance Impact on Performance</h3>
          <div className="period-selector">
            <select 
              value={selectedPeriod} 
              onChange={handlePeriodChange}
              className="period-dropdown"
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
        
        {correlationData && correlationData.correlation ? (
          <>
            <div className="correlation-bands">
              {renderAttendanceBand(
                correlationData.correlation.highAttendance,
                'High Attendance',
                '🏆'
              )}
              {renderAttendanceBand(
                correlationData.correlation.mediumAttendance,
                'Medium Attendance',
                '📈'
              )}
              {renderAttendanceBand(
                correlationData.correlation.lowAttendance,
                'Low Attendance',
                '⚠️'
              )}
            </div>
            
            {correlationData.insights && (
              <div className="correlation-insight">
                <div 
                  className="insight-strength"
                  style={{ 
                    backgroundColor: getCorrelationStrengthColor(correlationData.insights.correlationStrength),
                    color: 'white'
                  }}
                >
                  {correlationData.insights.correlationStrength.toUpperCase()}
                </div>
                <div className="insight-message">
                  💡 {formatInsightMessage(correlationData.insights)}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-data-state">
            <span className="no-data-icon">📊</span>
            <span className="no-data-message">No correlation data available for this period</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorrelationInsights;